/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import {
  Button,
  FormLabel,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Markdown from "markdown-to-jsx";
import { Box } from "@mui/system";
import React, { useEffect, useState } from "react";
import { CenteredTextWithUnderline } from "../../shared/components/reusableComp/CenteredTextWithUnderline";
import { downloadLink, getEnvQueryDetails } from "./apiRequest";
import CapthchGenerator from "./captchaGenerator";

const containerStyle = (matches) => css`
  margin: ${!matches ? "0% 20%" : "0 5%"};
  padding: 3.5% 0;
  text-align: center;
`;

const LabelTextCompStyle = css`
display: flex;
flex-direction: column;
margin-top: 6%;

}
`;

const captchaBoxStyle = css`
  display: flex;
  align-items: end;
  justify-content: space-between;
`;
const envWrapper = css`
  .MuiFormLabel-root,
  .MuiTypography-root,
  .MuiInputBase-root {
    font-family: "Roboto", "Helvetica", "Arial", sans-serif !important;
  }
`;

const buttonContainerStyle = css`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 5% 0;
  flex-direction: column;
  align-items: center;
`;
const typographyH6style = (theme) => css`
  font-size: 14px;
  line-height: 35px;
  font-family: ${theme.typography.MBCORPOA};
  img {
    width: 100%;
  }
`;

const LabelTextComp = ({
  label,
  placeholder,
  value,
  onChangeFunc,
  validate,
  helperText,
}) => (
  <Box css={LabelTextCompStyle}>
    <FormLabel sx={{ color: validate ? "red" : "black" }}>
      <strong>{label}</strong>
    </FormLabel>
    <TextField
      variant="standard"
      placeholder={placeholder}
      value={value}
      onChange={onChangeFunc}
      error={validate}
      helperText={validate ? helperText : " "}
    />
  </Box>
);

export const EnvQuery = () => {
  const [envQueryData, setEnvQueryData] = useState();
  const [captchaValue, setCaptchaValue] = useState("");
  const [vinNumber, setVinNumber] = useState("");
  const [captchaInput, setCaptchaInput] = useState("");
  const [validate, setValidate] = useState({
    captchaError: false,
    vinNumberError: false,
    formSubmitted: false,
  });

  const [loading, setLoading] = useState(false);

  const matches = useMediaQuery("(max-width: 415px)");

  useEffect(() => {
    getEnvQueryDetails().then(({ data }) => {
      document.title = data?.data?.attributes?.title;
      setEnvQueryData(data.data.attributes);
    });
  }, []);

  const downloadFunc = async (url, captchaErrorBool) => {
    await fetch(url).then((response) => {
      if (response.status === 200) {
        setValidate({
          ...validate,
          vinNumberError: false,
          captchaError: captchaErrorBool,
        });
        response.blob().then((blob) => {
          // Creating new object of PDF file
          const fileURL = window.URL.createObjectURL(blob);
          // Setting various property values
          let alink = document.createElement("a");
          alink.href = fileURL;
          alink.download = `${vinNumber}.pdf`;
          alink.click();
        });
      } else {
        setValidate({
          ...validate,
          vinNumberError: true,
          captchaError: captchaErrorBool,
        });
      }
    });
  };

  const validateFunc = () => {
    return new Promise((resolve, reject) => {
      const captchaErrorBool = captchaInput !== captchaValue;
      setValidate({
        ...validate,
        captchaError: captchaErrorBool,
        formSubmitted: true,
      });
      captchaErrorBool
        ? reject(new Error("Captch not matched"))
        : resolve(captchaErrorBool);
    });
  };

  const onSubmit = async () => {
    await setLoading(true);

    await validateFunc()
      .then((captchaErrorBool) =>
        downloadFunc(downloadLink(vinNumber), captchaErrorBool)
      )
      .catch((error) => {
        console.log(error);
      });
    await setLoading(false);
  };

  return (
    <Box
      sx={{ minHeight: matches ? "69vh" : "60vh", background: "white" }}
      css={envWrapper}
    >
      <Box css={() => containerStyle(matches)}>
        <CenteredTextWithUnderline
          title={envQueryData?.title}
          bottomMargin="5%"
          titleVariant="h5"
          titleColor="black"
          mobileUnderline="yes"
        />
        <span css={(theme) => typographyH6style(theme)}>
          <Markdown>
            {(process.env.REACT_APP_BASEURL.includes("localhost")
              ? envQueryData?.description?.replaceAll(
                  "/uploads/",
                  `${process.env.REACT_APP_BASEURL}/uploads/`
                )
              : envQueryData?.description) || ""}
          </Markdown>
        </span>

        {envQueryData && envQueryData.body.length > 0 && (
          <Box
            sx={{
              backgroundColor: "#fafafa",
              padding: "0 5%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                flexDirection: matches ? "column" : "row",
              }}
            >
              <LabelTextComp
                label={envQueryData?.body[0]?.vinNumber}
                placeholder={envQueryData?.body[0].vinPlaceholder}
                value={vinNumber}
                onChangeFunc={({ target }) => setVinNumber(target.value)}
                validate={validate.vinNumberError}
                helperText={envQueryData?.body[0].vinHelperText}
              />
              <Box css={captchaBoxStyle}>
                <LabelTextComp
                  label={envQueryData?.body[0]?.captcha}
                  placeholder={envQueryData?.body[0].captchaPlaceholder}
                  value={captchaInput}
                  onChangeFunc={({ target }) => setCaptchaInput(target.value)}
                  validate={validate.captchaError}
                  helperText={envQueryData?.body[0].captchaHelperText}
                />
                <CapthchGenerator
                  captchaValue={captchaValue}
                  setCaptchaValue={setCaptchaValue}
                  validate={!validate.formSubmitted || validate.captchaError}
                />
              </Box>
            </Box>
            <div css={buttonContainerStyle}>
              <Button
                variant="contained"
                id="submitId"
                sx={{ width: "15%", marginBottom: "2%", fontSize: "14px" }}
                onClick={onSubmit}
                disabled={loading}
              >
                {envQueryData?.body[0]?.buttonText}
              </Button>
              <Button
                sx={{ color: "#06a4ed", fontSize: "14px" }}
                onClick={() => {
                  setCaptchaInput("");
                  setVinNumber("");
                  setValidate({ captchaError: false, vinNumberError: false });
                }}
                disabled={loading}
              >
                {envQueryData?.body[0]?.clearFieldButtonText}
              </Button>
            </div>
          </Box>
        )}
        {validate.vinNumberError && (
          <>
            <CenteredTextWithUnderline
              title={envQueryData?.body[0]?.searchResultTitle}
              //description={envQueryData?.description}
              bottomMargin="5%"
              titleVariant="h5"
              titleColor="black"
              mobileUnderline="yes"
            />
            <Typography
              variant="body1"
              sx={{ textAlign: "center", color: "#06a4ed" }}
            >
              {envQueryData?.body[0]?.searchFailedText}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};
